import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  BarChart3, 
  Loader2, 
  TrendingUp,
  Sun,
  Moon,
  Calendar,
  RefreshCw,
  UtensilsCrossed,
  ChefHat,
  Coffee,
  Search,
  User,
  Archive,
  FileText,
  Trash2,
  RotateCcw,
  AlertTriangle,
  Clock,
  UserPlus,
  Home
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfWeek, startOfMonth, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Oggi' },
  { value: 'week', label: 'Questa settimana' },
  { value: 'month', label: 'Questo mese' },
  { value: 'last30', label: 'Ultimi 30 giorni' },
  { value: 'all', label: 'Tutto' }
];

const MEAL_OPTIONS = [
  { value: 'all', label: 'Tutti i pasti' },
  { value: 'colazione', label: 'Solo Colazione' },
  { value: 'pranzo', label: 'Solo Pranzo' },
  { value: 'cena', label: 'Solo Cena' }
];

const VARIANT_COLORS = {
  normale: 'bg-slate-100 text-slate-700',
  tritato: 'bg-purple-100 text-purple-700',
  pureato: 'bg-violet-100 text-violet-700',
  diabetico: 'bg-orange-100 text-orange-700'
};

const VARIANT_LABELS = {
  normale: 'Normale',
  tritato: 'Tritato',
  pureato: 'Pureato',
  diabetico: 'Diabetico'
};

export const OrderHistoryPage = () => {
  const { api, canViewArchive, isAdmin, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('statistiche');
  
  // Statistics state
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [mealType, setMealType] = useState('all');

  // Archive state
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveFilter, setArchiveFilter] = useState('archiviati'); // 'tutti', 'archiviati', 'attivi'

  // Trash (Cestino) state
  const [trashPatients, setTrashPatients] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashSearchQuery, setTrashSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Readmission (Riammissione) state
  const [readmitDialogOpen, setReadmitDialogOpen] = useState(false);
  const [patientToReadmit, setPatientToReadmit] = useState(null);
  const [readmitRoom, setReadmitRoom] = useState('');
  const [readmitStayFrom, setReadmitStayFrom] = useState('');
  const [readmitStayUntil, setReadmitStayUntil] = useState('');

  const getDateRange = useCallback(() => {
    const today = new Date();
    let startDate = null;
    let endDate = format(today, 'yyyy-MM-dd');

    switch (period) {
      case 'today':
        startDate = endDate;
        break;
      case 'week':
        startDate = format(startOfWeek(today, { locale: it }), 'yyyy-MM-dd');
        break;
      case 'month':
        startDate = format(startOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'last30':
        startDate = format(subDays(today, 30), 'yyyy-MM-dd');
        break;
      case 'all':
      default:
        startDate = null;
        endDate = null;
        break;
    }

    return { startDate, endDate };
  }, [period]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (mealType !== 'all') params.meal_type = mealType;

      const response = await api.get('/orders/statistics', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  }, [api, getDateRange, mealType]);

  const fetchPatients = useCallback(async () => {
    try {
      // Use archive endpoint to get ALL patients (active + archived)
      const response = await api.get('/patients/archive');
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      // Fallback to regular patients if archive not accessible
      try {
        const fallbackResponse = await api.get('/patients');
        setPatients(fallbackResponse.data);
      } catch (e) {
        console.error('Fallback failed:', e);
      }
    }
  }, [api]);

  const fetchPatientHistory = useCallback(async (patientId) => {
    setArchiveLoading(true);
    try {
      const response = await api.get(`/orders/patient/${patientId}/history`);
      setPatientHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch patient history:', error);
      toast.error('Errore nel caricamento dello storico paziente');
    } finally {
      setArchiveLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === 'archivio') {
      fetchPatients();
    }
  }, [activeTab, fetchPatients]);

  useEffect(() => {
    if (selectedPatient) {
      fetchPatientHistory(selectedPatient.id);
    }
  }, [selectedPatient, fetchPatientHistory]);

  const filteredPatients = patients.filter(p => {
    // First apply status filter
    if (archiveFilter === 'archiviati' && p.status !== 'archiviato') return false;
    if (archiveFilter === 'attivi' && p.status === 'archiviato') return false;
    // Then apply search
    return p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.room.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.patient_code && p.patient_code.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  // Group patient history by date
  const groupedHistory = patientHistory.reduce((acc, order) => {
    const date = order.date;
    if (!acc[date]) {
      acc[date] = { colazione: [], pranzo: [], cena: [] };
    }
    if (order.meal_type && acc[date][order.meal_type]) {
      acc[date][order.meal_type].push(order);
    }
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedHistory).sort((a, b) => new Date(b) - new Date(a));

  // Trash functions
  const fetchTrashPatients = useCallback(async () => {
    setTrashLoading(true);
    try {
      const response = await api.get('/patients/trash');
      setTrashPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch trash patients:', error);
    } finally {
      setTrashLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (activeTab === 'cestino') {
      fetchTrashPatients();
    }
  }, [activeTab, fetchTrashPatients]);

  const filteredTrashPatients = trashPatients.filter(p => {
    return p.name.toLowerCase().includes(trashSearchQuery.toLowerCase()) ||
      p.room.toLowerCase().includes(trashSearchQuery.toLowerCase()) ||
      (p.patient_code && p.patient_code.toLowerCase().includes(trashSearchQuery.toLowerCase()));
  });

  const handleRestorePatient = async (patientId) => {
    setActionLoading(true);
    try {
      await api.post(`/patients/${patientId}/restore`);
      toast.success('Paziente ripristinato nell\'archivio');
      fetchTrashPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel ripristino');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteDialog = (patient) => {
    setPatientToDelete(patient);
    setDeleteDialogOpen(true);
  };

  const handleMoveToTrash = async (patient, e) => {
    e.stopPropagation(); // Prevent triggering the parent button click
    setActionLoading(true);
    try {
      await api.post(`/patients/${patient.id}/trash`);
      toast.success('Paziente spostato nel cestino');
      fetchPatients();
      // Also refresh trash count
      fetchTrashPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello spostamento');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!patientToDelete) return;
    setActionLoading(true);
    try {
      await api.delete(`/patients/${patientToDelete.id}/permanent`);
      toast.success('Paziente eliminato definitivamente');
      setDeleteDialogOpen(false);
      setPatientToDelete(null);
      fetchTrashPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setActionLoading(false);
    }
  };

  // Open readmit dialog
  const openReadmitDialog = (patient, e) => {
    e.stopPropagation();
    setPatientToReadmit(patient);
    setReadmitRoom(patient.room || '');
    setReadmitStayFrom(format(new Date(), 'yyyy-MM-dd'));
    setReadmitStayUntil('');
    setReadmitDialogOpen(true);
  };

  // Handle patient readmission
  const handleReadmitPatient = async () => {
    if (!patientToReadmit || !readmitRoom || !readmitStayFrom) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post(`/patients/${patientToReadmit.id}/readmit`, {
        room: readmitRoom,
        stay_from: readmitStayFrom,
        stay_until: readmitStayUntil || null
      });
      toast.success(`${patientToReadmit.name} è stato riammesso in camera ${readmitRoom}`);
      setReadmitDialogOpen(false);
      setPatientToReadmit(null);
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella riammissione');
    } finally {
      setActionLoading(false);
    }
  };

  const totalOrders = stats?.total_orders || 0;
  const maxCategoryCount = stats?.categories?.length > 0 
    ? Math.max(...stats.categories.map(c => c.count)) 
    : 1;

  if (loading && activeTab === 'statistiche') {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Storico Ordini</h1>
            <p className="text-slate-500">Analisi delle comande e categorie più ordinate</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="statistiche" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Statistiche
          </TabsTrigger>
          {canViewArchive && (
            <TabsTrigger value="archivio" className="flex items-center gap-2">
              <Archive className="w-4 h-4" />
              Archivio
            </TabsTrigger>
          )}
          {canViewArchive && (
            <TabsTrigger value="cestino" className="flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Cestino
              {trashPatients.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs bg-red-100 text-red-700">
                  {trashPatients.length}
                </Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Statistiche Tab */}
        <TabsContent value="statistiche" className="space-y-6 mt-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[180px]" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <UtensilsCrossed className="w-4 h-4 text-slate-500" />
              <Select value={mealType} onValueChange={setMealType}>
                <SelectTrigger className="w-[180px]" data-testid="meal-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEAL_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={fetchStats} data-testid="refresh-stats">
              <RefreshCw className="w-4 h-4 mr-2" />
              Aggiorna
            </Button>
          </div>

          {/* Stats Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-slate-900">{totalOrders}</div>
                <p className="text-sm text-slate-500">Ordini totali</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-slate-900">
                  {stats?.variants?.normale || 0}
                </div>
                <p className="text-sm text-slate-500">Normali</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-slate-900">
                  {(stats?.variants?.tritato || 0) + (stats?.variants?.pureato || 0)}
                </div>
                <p className="text-sm text-slate-500">Tritati/Pureati</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-slate-900">
                  {stats?.variants?.diabetico || 0}
                </div>
                <p className="text-sm text-slate-500">Diabetici</p>
              </CardContent>
            </Card>
          </div>

          {/* Categories Chart */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Categorie più ordinate
              </CardTitle>
              <CardDescription>
                Classifica delle categorie di piatti per numero di ordini
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!stats?.categories?.length ? (
                <div className="py-12 text-center text-slate-500">
                  <ChefHat className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nessun ordine nel periodo selezionato</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {stats.categories.map((cat, index) => (
                    <div key={cat.code} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-xs font-bold flex items-center justify-center">
                            {index + 1}
                          </span>
                          <span className="font-medium text-slate-900">{cat.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Coffee className="w-4 h-4 text-amber-500" />
                            <span className="text-slate-600">{cat.colazione || 0}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Sun className="w-4 h-4 text-orange-500" />
                            <span className="text-slate-600">{cat.pranzo}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Moon className="w-4 h-4 text-indigo-500" />
                            <span className="text-slate-600">{cat.cena}</span>
                          </div>
                          <Badge variant="secondary" className="font-bold">
                            {cat.count}
                          </Badge>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-600 to-sky-400 rounded-full transition-all duration-500"
                          style={{ width: `${(cat.count / maxCategoryCount) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Variants Distribution */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-800">
                Distribuzione Varianti
              </CardTitle>
              <CardDescription>
                Percentuale di ordini per tipo di preparazione
              </CardDescription>
            </CardHeader>
            <CardContent>
              {totalOrders === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessun dato</p>
              ) : (
                <div className="flex flex-wrap gap-4">
                  {Object.entries(stats?.variants || {}).map(([variant, count]) => (
                    <div key={variant} className="flex-1 min-w-[120px]">
                      <div className={`p-4 rounded-lg ${VARIANT_COLORS[variant] || 'bg-slate-100'}`}>
                        <div className="text-2xl font-bold">{count}</div>
                        <div className="text-sm">{VARIANT_LABELS[variant] || variant}</div>
                        <div className="text-xs opacity-75 mt-1">
                          {((count / totalOrders) * 100).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Daily Trend */}
          {stats?.daily?.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800">
                  Andamento Giornaliero
                </CardTitle>
                <CardDescription>
                  Ultimi giorni con ordini registrati
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                        <th className="pb-3 pr-4">Data</th>
                        <th className="pb-3 pr-4 text-center">
                          <Coffee className="w-4 h-4 inline text-amber-500" /> Col
                        </th>
                        <th className="pb-3 pr-4 text-center">
                          <Sun className="w-4 h-4 inline text-orange-500" /> Pranzo
                        </th>
                        <th className="pb-3 pr-4 text-center">
                          <Moon className="w-4 h-4 inline text-indigo-500" /> Cena
                        </th>
                        <th className="pb-3 text-center">Totale</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {stats.daily.slice(0, 10).map(day => (
                        <tr key={day.date} className="hover:bg-slate-50">
                          <td className="py-3 pr-4 font-medium text-slate-900">
                            {format(new Date(day.date), 'EEE d MMM', { locale: it })}
                          </td>
                          <td className="py-3 pr-4 text-center text-amber-600 font-semibold">
                            {day.colazione || 0}
                          </td>
                          <td className="py-3 pr-4 text-center text-orange-600 font-semibold">
                            {day.pranzo}
                          </td>
                          <td className="py-3 pr-4 text-center text-indigo-600 font-semibold">
                            {day.cena}
                          </td>
                          <td className="py-3 text-center font-bold text-slate-900">
                            {day.total}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Archivio Pazienti Tab */}
        <TabsContent value="archivio" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Patient Search Panel */}
            <Card className="border-slate-200 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-600" />
                  Cerca Paziente
                </CardTitle>
                <CardDescription>
                  Cerca per nome, camera o codice
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filter buttons */}
                <div className="flex gap-1">
                  <Button
                    variant={archiveFilter === 'archiviati' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setArchiveFilter('archiviati')}
                    className={archiveFilter === 'archiviati' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    Archiviati
                  </Button>
                  <Button
                    variant={archiveFilter === 'attivi' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setArchiveFilter('attivi')}
                    className={archiveFilter === 'attivi' ? 'bg-green-500 hover:bg-green-600' : ''}
                  >
                    Attivi
                  </Button>
                  <Button
                    variant={archiveFilter === 'tutti' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setArchiveFilter('tutti')}
                    className={archiveFilter === 'tutti' ? 'bg-blue-500 hover:bg-blue-600' : ''}
                  >
                    Tutti
                  </Button>
                </div>
                
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Nome, camera o codice..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="patient-search-input"
                  />
                </div>
                
                <p className="text-xs text-slate-500">
                  {filteredPatients.length} pazient{filteredPatients.length === 1 ? 'e' : 'i'} trovat{filteredPatients.length === 1 ? 'o' : 'i'}
                </p>
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {filteredPatients.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">Nessun paziente trovato</p>
                  ) : (
                    filteredPatients.map(patient => (
                      <div
                        key={patient.id}
                        className={`w-full text-left p-3 rounded-lg border transition-all overflow-visible ${
                          selectedPatient?.id === patient.id
                            ? 'border-blue-500 bg-blue-50'
                            : patient.status === 'archiviato'
                              ? 'border-orange-200 bg-orange-50/50 hover:border-orange-300'
                              : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                        data-testid={`patient-archive-${patient.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedPatient(patient)}
                            className="flex-1 flex items-center gap-3 text-left min-w-0"
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              patient.status === 'archiviato' ? 'bg-orange-100' : 'bg-slate-100'
                            }`}>
                              <User className={`w-5 h-5 ${patient.status === 'archiviato' ? 'text-orange-600' : 'text-slate-600'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900 truncate">{patient.name}</span>
                                {patient.status === 'archiviato' && (
                                  <Badge className="bg-orange-100 text-orange-700 text-xs">
                                    Archiviato
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-slate-500 flex items-center gap-2 flex-wrap">
                                <span>Camera {patient.room}</span>
                                {patient.patient_code && (
                                  <Badge variant="outline" className="text-xs font-mono">
                                    {patient.patient_code}
                                  </Badge>
                                )}
                                {patient.discharged_at && (
                                  <span className="text-orange-600 text-xs">
                                    Uscito: {format(parseISO(patient.discharged_at), 'd MMM yyyy', { locale: it })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                          {/* Buttons for archived patients */}
                          {patient.status === 'archiviato' && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {/* Readmit button */}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => openReadmitDialog(patient, e)}
                                disabled={actionLoading}
                                className="h-8 w-8 text-green-500 hover:text-green-600 hover:bg-green-50"
                                title="Riammetti paziente"
                                data-testid={`readmit-patient-${patient.id}`}
                              >
                                <UserPlus className="w-4 h-4" />
                              </Button>
                              {/* Move to trash button */}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => handleMoveToTrash(patient, e)}
                                disabled={actionLoading}
                                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                title="Sposta nel cestino"
                                data-testid={`move-to-trash-${patient.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Patient History Panel */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  {selectedPatient ? `Storico di ${selectedPatient.name}` : 'Seleziona un paziente'}
                </CardTitle>
                {selectedPatient && (
                  <CardDescription>
                    Camera {selectedPatient.room}
                    {selectedPatient.patient_code && ` • Codice: ${selectedPatient.patient_code}`}
                    {selectedPatient.stay_from && ` • Dal: ${format(parseISO(selectedPatient.stay_from), 'd MMM yyyy', { locale: it })}`}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {!selectedPatient ? (
                  <div className="py-12 text-center text-slate-500">
                    <Archive className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Seleziona un paziente dalla lista per visualizzare il suo storico</p>
                  </div>
                ) : archiveLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Stay History Section */}
                    {(selectedPatient.stay_history?.length > 0 || selectedPatient.stay_from) && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-blue-50 px-4 py-2 font-medium text-blue-700 flex items-center gap-2">
                          <Home className="w-4 h-4" />
                          Storico Permanenze
                        </div>
                        <div className="p-4 space-y-3">
                          {/* Current/Last stay */}
                          <div className={`p-3 rounded-lg border ${selectedPatient.status === 'attivo' ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Home className="w-4 h-4 text-slate-600" />
                                <span className="font-medium">Camera {selectedPatient.room}</span>
                                {selectedPatient.status === 'attivo' && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">Attuale</Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-sm text-slate-600 mt-1">
                              {selectedPatient.stay_from ? (
                                <>
                                  Dal {format(parseISO(selectedPatient.stay_from), 'd MMM yyyy', { locale: it })}
                                  {selectedPatient.stay_until && (
                                    <> al {format(parseISO(selectedPatient.stay_until), 'd MMM yyyy', { locale: it })}</>
                                  )}
                                  {selectedPatient.discharged_at && selectedPatient.status !== 'attivo' && (
                                    <span className="text-orange-600 ml-2">
                                      (Uscito: {format(parseISO(selectedPatient.discharged_at), 'd MMM yyyy', { locale: it })})
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="text-slate-400">Date non specificate</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Previous stays */}
                          {selectedPatient.stay_history?.length > 0 && (
                            <>
                              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Permanenze Precedenti</p>
                              {[...selectedPatient.stay_history].reverse().map((stay, idx) => (
                                <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                                  <div className="flex items-center gap-2">
                                    <Home className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium text-slate-700">Camera {stay.room}</span>
                                  </div>
                                  <div className="text-sm text-slate-500 mt-1">
                                    {stay.stay_from ? (
                                      <>
                                        Dal {format(parseISO(stay.stay_from), 'd MMM yyyy', { locale: it })}
                                        {stay.stay_until && (
                                          <> al {format(parseISO(stay.stay_until), 'd MMM yyyy', { locale: it })}</>
                                        )}
                                        {stay.discharged_at && (
                                          <span className="text-orange-600 ml-2">
                                            (Uscito: {format(parseISO(stay.discharged_at), 'd MMM yyyy', { locale: it })})
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-slate-400">Date non specificate</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Orders History */}
                    {sortedDates.length === 0 ? (
                      <div className="py-8 text-center text-slate-500">
                        <ChefHat className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                        <p>Nessun ordine registrato per questo paziente</p>
                      </div>
                    ) : (
                      <div className="space-y-4 max-h-[400px] overflow-y-auto">
                        {sortedDates.map(date => (
                          <div key={date} className="border rounded-lg overflow-hidden">
                            <div className="bg-slate-100 px-4 py-2 font-medium text-slate-700">
                              {format(parseISO(date), 'EEEE d MMMM yyyy', { locale: it })}
                            </div>
                            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* Colazione */}
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-amber-600 font-medium">
                                  <Coffee className="w-4 h-4" />
                                  Colazione
                                </div>
                                {groupedHistory[date].colazione.length === 0 ? (
                                  <p className="text-sm text-slate-400">-</p>
                                ) : (
                                  groupedHistory[date].colazione.map((order, idx) => (
                                    <div key={idx} className="text-sm bg-amber-50 p-2 rounded">
                                      <div className="font-medium">{order.dish_name}</div>
                                      {order.condition_variant !== 'normale' && (
                                        <Badge className={`text-xs ${VARIANT_COLORS[order.condition_variant]}`}>
                                          {VARIANT_LABELS[order.condition_variant]}
                                        </Badge>
                                      )}
                                      {order.note && <p className="text-amber-700 text-xs mt-1">{order.note}</p>}
                                    </div>
                                  ))
                                )}
                              </div>
                              {/* Pranzo */}
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-orange-600 font-medium">
                                  <Sun className="w-4 h-4" />
                                  Pranzo
                                </div>
                                {groupedHistory[date].pranzo.length === 0 ? (
                                  <p className="text-sm text-slate-400">-</p>
                                ) : (
                                  groupedHistory[date].pranzo.map((order, idx) => (
                                    <div key={idx} className="text-sm bg-orange-50 p-2 rounded">
                                      <div className="font-medium">{order.dish_name}</div>
                                      {order.condition_variant !== 'normale' && (
                                        <Badge className={`text-xs ${VARIANT_COLORS[order.condition_variant]}`}>
                                          {VARIANT_LABELS[order.condition_variant]}
                                        </Badge>
                                      )}
                                      {order.note && <p className="text-orange-700 text-xs mt-1">{order.note}</p>}
                                    </div>
                                  ))
                                )}
                              </div>
                              {/* Cena */}
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-indigo-600 font-medium">
                                  <Moon className="w-4 h-4" />
                                  Cena
                                </div>
                                {groupedHistory[date].cena.length === 0 ? (
                                  <p className="text-sm text-slate-400">-</p>
                                ) : (
                                  groupedHistory[date].cena.map((order, idx) => (
                                    <div key={idx} className="text-sm bg-indigo-50 p-2 rounded">
                                      <div className="font-medium">{order.dish_name}</div>
                                      {order.condition_variant !== 'normale' && (
                                        <Badge className={`text-xs ${VARIANT_COLORS[order.condition_variant]}`}>
                                          {VARIANT_LABELS[order.condition_variant]}
                                        </Badge>
                                      )}
                                      {order.note && <p className="text-indigo-700 text-xs mt-1">{order.note}</p>}
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Cestino Tab */}
        <TabsContent value="cestino" className="space-y-6 mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-600" />
                Cestino
              </CardTitle>
              <CardDescription>
                I pazienti nel cestino verranno eliminati definitivamente dopo 30 giorni. Puoi ripristinarli o eliminarli prima.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Cerca paziente..."
                  value={trashSearchQuery}
                  onChange={(e) => setTrashSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="trash-search-input"
                />
              </div>

              {trashLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-red-600" />
                </div>
              ) : filteredTrashPatients.length === 0 ? (
                <div className="py-12 text-center text-slate-500">
                  <Trash2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Il cestino è vuoto</p>
                  <p className="text-sm mt-1">I pazienti eliminati dall'archivio appariranno qui</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTrashPatients.map(patient => (
                    <div 
                      key={patient.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-red-200 bg-red-50/50"
                      data-testid={`trash-patient-${patient.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-red-100">
                          <User className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{patient.name}</div>
                          <div className="text-sm text-slate-500 flex items-center gap-2 flex-wrap">
                            <span>Camera {patient.room}</span>
                            {patient.patient_code && (
                              <Badge variant="outline" className="text-xs font-mono">
                                {patient.patient_code}
                              </Badge>
                            )}
                          </div>
                          {patient.deleted_at && (
                            <div className="flex items-center gap-1 mt-1 text-xs">
                              <Clock className="w-3 h-3 text-orange-500" />
                              <span className="text-orange-600">
                                {patient.days_remaining > 0 
                                  ? `Eliminazione automatica tra ${patient.days_remaining} giorni`
                                  : 'Eliminazione imminente'
                                }
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestorePatient(patient.id)}
                          disabled={actionLoading}
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          data-testid={`restore-patient-${patient.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Ripristina
                        </Button>
                        {(isAdmin || isSuperAdmin) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDeleteDialog(patient)}
                            disabled={actionLoading}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            data-testid={`delete-patient-${patient.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Elimina
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Eliminazione Definitiva
            </DialogTitle>
          </DialogHeader>
          {patientToDelete && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-slate-700">
                  Stai per eliminare <strong>definitivamente</strong> il paziente:
                </p>
                <p className="text-xl font-bold text-slate-900 mt-2">{patientToDelete.name}</p>
                <p className="text-sm text-slate-500">Camera {patientToDelete.room}</p>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">
                  <strong>Attenzione:</strong> Questa azione è irreversibile. Verranno eliminati anche tutti gli ordini associati al paziente.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setPatientToDelete(null);
                  }}
                  disabled={actionLoading}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handlePermanentDelete}
                  disabled={actionLoading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Elimina Definitivamente
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Readmit Patient Dialog */}
      <Dialog open={readmitDialogOpen} onOpenChange={setReadmitDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <UserPlus className="w-5 h-5" />
              Riammetti Paziente
            </DialogTitle>
          </DialogHeader>
          {patientToReadmit && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-slate-700">
                  Riammetti il paziente nella struttura:
                </p>
                <p className="text-xl font-bold text-slate-900 mt-2">{patientToReadmit.name}</p>
                {patientToReadmit.stay_history?.length > 0 && (
                  <p className="text-sm text-slate-500 mt-1">
                    {patientToReadmit.stay_history.length} permanenz{patientToReadmit.stay_history.length === 1 ? 'a' : 'e'} precedent{patientToReadmit.stay_history.length === 1 ? 'e' : 'i'}
                  </p>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="readmit-room" className="flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    Camera *
                  </Label>
                  <Input
                    id="readmit-room"
                    placeholder="Es: 201"
                    value={readmitRoom}
                    onChange={(e) => setReadmitRoom(e.target.value)}
                    data-testid="readmit-room-input"
                  />
                  {patientToReadmit.room && readmitRoom !== patientToReadmit.room && (
                    <p className="text-xs text-slate-500">
                      Camera precedente: {patientToReadmit.room}
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="readmit-from" className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Dal *
                    </Label>
                    <Input
                      id="readmit-from"
                      type="date"
                      value={readmitStayFrom}
                      onChange={(e) => setReadmitStayFrom(e.target.value)}
                      data-testid="readmit-from-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="readmit-until">
                      Al (opzionale)
                    </Label>
                    <Input
                      id="readmit-until"
                      type="date"
                      value={readmitStayUntil}
                      onChange={(e) => setReadmitStayUntil(e.target.value)}
                      min={readmitStayFrom}
                      data-testid="readmit-until-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setReadmitDialogOpen(false);
                    setPatientToReadmit(null);
                  }}
                  disabled={actionLoading}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handleReadmitPatient}
                  disabled={actionLoading || !readmitRoom || !readmitStayFrom}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Riammettendo...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Riammetti
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
